import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Wordmark } from "./Wordmark";

const links = [
  { to: "/", label: "Home", id: "home" },
  { to: "/shows", label: "Shows", id: "shows" },
  { to: "/media", label: "Media", id: "media" },
  { to: "/about", label: "About", id: "about" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          scrolled ? "glass-dark border-b border-amber-500/15 shadow-[0_8px_32px_rgba(0,0,0,0.5)]" : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 h-20 flex items-center justify-between">
          <Wordmark />
          <nav className="hidden md:flex items-center gap-8" aria-label="Primary">
            {links.map((l) => (
              <NavLink
                key={l.id}
                to={l.to}
                data-testid={`nav-link-${l.id}`}
                className={({ isActive }) =>
                  `font-mono text-xs uppercase tracking-[0.25em] transition-colors duration-300 ${
                    isActive ? "text-amber-300" : "text-slate-400 hover:text-amber-200"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/booking"
              data-testid="nav-book-band-btn"
              className="font-mono text-xs uppercase tracking-[0.2em] px-5 py-2.5 bg-gold text-midnight hover:bg-gold-bright transition-colors duration-300"
            >
              Book the Band
            </Link>
          </nav>
          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden text-amber-200 p-2"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Close menu" : "Open menu"}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-40 glass-dark flex flex-col items-center justify-center gap-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
          >
            {[...links, { to: "/booking", label: "Booking", id: "booking" }].map((l, i) => (
              <motion.div
                key={l.id}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.08 * i, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              >
                <NavLink
                  to={l.to}
                  data-testid={`mobile-nav-link-${l.id}`}
                  className="font-serif text-4xl text-amber-100 tracking-wide"
                >
                  {l.label}
                </NavLink>
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
